// -----------------------------------------------------------
// Author  : Mathieu Bucher
// Resume  : Deduplicate Vision images using visual fingerprinting
// Date    : 2026-03-17
// Ref.    : feat/spike-pdf-vision
// -----------------------------------------------------------
import { useEffect, useState } from "react";
import type { UsedSource } from "@noa/api-core";
import { buildAuthHeaders, getBaseUrl } from "@noa/api-client";

/**
 * Resolve a relative /api/... path to the full API URL.
 * In production, the frontend and API are on different domains, so relative
 * URLs must be prefixed with the API base URL.
 */
function resolveApiUrl(url: string): string {
  const isExternal = url.startsWith("http://") || url.startsWith("https://");
  if (isExternal) return url;
  // Relative /api/... paths need the API base URL prefix
  if (url.startsWith("/api/")) {
    return `${getBaseUrl()}${url}`;
  }
  return url;
}

/**
 * Compute a visual fingerprint by drawing the image onto a tiny 8x8 canvas.
 * Uses authenticated fetch for internal API URLs.
 */
const computeVisualFingerprint = async (src: string): Promise<string> => {
  try {
    // Fetch image with auth for internal URLs
    const isInternal = !src.startsWith("http://") && !src.startsWith("https://");
    let blobUrl: string;

    if (isInternal) {
      const headers = await buildAuthHeaders();
      const fullUrl = resolveApiUrl(src);
      const response = await fetch(fullUrl, { headers });
      if (!response.ok) throw new Error(`Failed to fetch image: ${response.status}`);
      const blob = await response.blob();
      blobUrl = URL.createObjectURL(blob);
    } else {
      blobUrl = src;
    }

    return await new Promise<string>((resolve) => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = 8;
        canvas.height = 8;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(src);
        } else {
          ctx.drawImage(img, 0, 0, 8, 8);
          resolve(canvas.toDataURL());
        }
        if (isInternal) URL.revokeObjectURL(blobUrl);
      };
      img.onerror = () => {
        if (isInternal) URL.revokeObjectURL(blobUrl);
        resolve(src);
      };
      img.src = blobUrl;
    });
  } catch {
    return src;
  }
};

/**
 * Hook that deduplicates used_sources images using visual fingerprinting.
 * Returns unique image sources and a Set of their fingerprints for cross-channel dedup.
 */
export const useDeduplicatedImages = (usedSources: UsedSource[] | undefined) => {
  const [uniqueSources, setUniqueSources] = useState<UsedSource[]>([]);
  const [fingerprints, setFingerprints] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    let cancelled = false;

    const dedup = async () => {
      const sources = usedSources?.filter((s) => s.image_url) ?? [];
      if (sources.length === 0) {
        if (!cancelled) {
          setUniqueSources([]);
          setFingerprints(new Map());
        }
        return;
      }

      const seen = new Set<string>();
      const unique: UsedSource[] = [];
      const fpMap = new Map<string, string>();

      for (const source of sources) {
        const fp = await computeVisualFingerprint(source.image_url!);
        fpMap.set(source.image_url!, fp);
        if (!seen.has(fp)) {
          seen.add(fp);
          unique.push(source);
        }
      }

      if (!cancelled) {
        setUniqueSources(unique);
        setFingerprints(fpMap);
      }
    };

    dedup();
    return () => {
      cancelled = true;
    };
  }, [usedSources]);

  const isImageDuplicate = (src: string): boolean => {
    // Exact URL match against used_sources image URLs (sufficient for cross-channel dedup
    // since the agent generates the same base64 URL in markdown as in used_sources)
    return fingerprints.has(src);
  };

  return { uniqueSources, isImageDuplicate };
};
